import React, { useCallback, useState } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useMutation, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { useEnsureContext } from 'common/hooks';
import { message } from 'antd';
import { FormikOnSubmit } from 'lib/formik.utils';
import { BusinessPartnerMedicalOperatorForm } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/shared/BusinessPartnerMedicalOperatorForm';
import { MedicalOperatorFormModel } from 'models/medicalOperator';
import { Header } from 'common/components/Header';
import { useHistory } from 'react-router';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { BusinessPartnerRoutes } from 'pages/BusinessPartnerManagement/businessPartnerRoutes';

export const BusinessPartnerRegisterMedicalOperatorPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const queryCache = useQueryCache();

  const [mutate, { isLoading }] = useMutation(
    BusinessPartnersAPI.registerNewMedicalOperator
  );

  const [mutateuserPermissions] = useMutation(
    BusinessPartnersAPI.updateUserPermissions
  );

  const [checkedDenyAccessToApps, setCheckedDenyAccessToApps] = useState(false);
  const [
    checkedDenyAccessToPatients,
    setCheckedDenyAccessToPatients,
  ] = useState(false);
  const [
    checkedDenyAccessToHigProOne,
    setCheckedDenyAccessToHigoProOne,
  ] = useState(false);

  const history = useHistory();
  const intlMessage = useFormatMessage();

  const appPermissionItems = [
    {
      permission: 'ADMIN_PANEL_ACCESS',
      scope: 'PARTNER',
      enabled: !checkedDenyAccessToApps,
      read: !checkedDenyAccessToApps,
      create: !checkedDenyAccessToApps,
      update: !checkedDenyAccessToApps,
      delete: !checkedDenyAccessToApps,
    },
    {
      permission: 'DOCTOR_PORTAL_ACCESS',
      scope: 'PARTNER',
      enabled: !checkedDenyAccessToApps,
      read: !checkedDenyAccessToApps,
      create: !checkedDenyAccessToApps,
      update: !checkedDenyAccessToApps,
      delete: !checkedDenyAccessToApps,
    },
  ];

  const patientPermissionItems = [
    {
      permission: 'PATIENT',
      scope: 'PARTNER',
      enabled: true,
      read: true,
      create: !checkedDenyAccessToPatients,
      update: !checkedDenyAccessToPatients,
      delete: !checkedDenyAccessToPatients,
    },
  ];

  const higoProOnePermissionItems = [
    {
      permission: 'PATIENT_ASSIGNMENT',
      scope: 'PARTNER',
      enabled: true,
      read: true,
      create: !checkedDenyAccessToHigProOne,
      update: !checkedDenyAccessToHigProOne,
      delete: !checkedDenyAccessToHigProOne,
    },
  ];

  const removeSpacesFromForm = (formData: MedicalOperatorFormModel) => {
    formData.phoneNumber = formData.phoneNumber

      .replace(/\s/g, '')
      .replace(/^0+/, '');
    formData.prefixCode = formData.prefixCode.replace(/\s/g, '');
    return formData;
  };

  const handleSubmit: FormikOnSubmit<MedicalOperatorFormModel> = useCallback(
    (formData, { resetForm }) => {
      const formDataToSend =
        formData.phoneNumber !== null
          ? removeSpacesFromForm(formData)
          : formData;

      mutate(
        {
          businessPartnerId: formDataToSend.partner
            ? formDataToSend.partner
            : bpContext.id,
          dto: formDataToSend,
        },
        {
          onSuccess: (res) => {
            checkedDenyAccessToApps &&
              mutateuserPermissions({
                userId: res.userId,
                items: appPermissionItems,
              });
            checkedDenyAccessToPatients &&
              mutateuserPermissions({
                userId: res.userId,
                items: patientPermissionItems,
              });

            checkedDenyAccessToHigProOne &&
              mutateuserPermissions({
                userId: res.userId,
                items: higoProOnePermissionItems,
              });
            resetForm();
            queryCache
              .invalidateQueries(QueryKey.BusinessPartnerMedicalOperatorsList)
              .then();
            message.success(
              intlMessage(
                { id: 'medicalOperators.register.success' },
                {
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                }
              )
            );
            history.push(`../${BusinessPartnerRoutes.MedicalOperators}`);
          },
        }
      ).then();
    },
    [
      bpContext,
      mutate,
      queryCache,
      intlMessage,
      history,
      checkedDenyAccessToApps,
      mutateuserPermissions,
      appPermissionItems,
      patientPermissionItems,
      checkedDenyAccessToPatients,
      checkedDenyAccessToHigProOne,
      higoProOnePermissionItems,
    ]
  );
  return (
    <>
      <Header title={intlMessage({ id: 'medicalOperators.new' })} />
      <BusinessPartnerMedicalOperatorForm
        onSubmit={handleSubmit}
        loading={isLoading}
        setCheckedDenyAccessToApps={setCheckedDenyAccessToApps}
        setCheckedDenyAccessToPatients={setCheckedDenyAccessToPatients}
        setCheckedDenyAccessToHigoProOne={setCheckedDenyAccessToHigoProOne}
      />
    </>
  );
};
